import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Row } from 'antd';
import usecustomStyles from '../../Common/customStyles';
import Widget from './Widget';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAnalyticsCount, getBannerAds } from '../../slices/ads/thunk';
import { businessInfo } from '../../slices/businessInfo/thunk';
import AddIcon from '../../assets/images/add_icon.png';
import Warning from '../../Common/Warning/Warning';
import NoResults from '../Components/NoResults/NoResults';
import RejectedUi from './RejectedUi/RejectedUi';
import Banner from '../../assets/images/banner.png'
import useProfileCache from '../../Common/Hooks/useProfileCache';

const Dashboard = () => {
  const customStyles = usecustomStyles(); // should be inside component

  document.title = "Dashboard"; 

  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const analyticsCount = useSelector((state) => state.Profiles.analyticsCount);
  const bannerAds = useSelector((state) => state.Profiles.bannerAds);
  const loading = useSelector((state) => state.Profiles.loading);
  const bureauData = useSelector((state) => state.BusinessInfoReducer?.businessInfo);
  const bureauLoading = useSelector((state) => state.BusinessInfoReducer?.loading);


useEffect(()=>{
  dispatch(businessInfo());
},[])
  useEffect(() => {
    dispatch(getAnalyticsCount());
    dispatch(getBannerAds());
    dispatch(businessInfo());
    
  }, [dispatch]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);

  // Profiles: fetch on dashboard for count-based UI
  const {
    profiles: ads,
    loading: profilesLoading,
    fetchProfiles,
    getCachedProfiles,
    cacheStatus
  } = useProfileCache();

  useEffect(() => {
    // try cached first
    const cached = getCachedProfiles();
    if (cached && cached.data && cached.data.length > 0) {
      return;
    }
    // fetch first page for count
    fetchProfiles({ page: 1, limit: 6 }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalProfilesCount = useMemo(() => {
    if (profilesLoading) return 0;
    if (Array.isArray(ads) && ads.length > 0) return ads.length;
    if (typeof cacheStatus?.total === 'number') return cacheStatus.total;
    return 0;
  }, [ads, cacheStatus?.total, profilesLoading]);

  // Stabilize count to avoid flicker back to 0 during transient loading
  const [stableProfilesCount, setStableProfilesCount] = useState(0);
  const hasInitializedCountRef = useRef(false);

  useEffect(() => {
    if (totalProfilesCount > 0) {
      setStableProfilesCount(totalProfilesCount);
      hasInitializedCountRef.current = true;
    } else if (!profilesLoading && !hasInitializedCountRef.current) {
      // First resolution with 0 and never had >0
      setStableProfilesCount(0);
    }
  }, [totalProfilesCount, profilesLoading]);

  // Log the profile count and ids whenever it changes
  useEffect(() => {
    const ids = Array.isArray(ads) ? ads.map(item => item?.id) : [];
    console.info('[Dashboard] Profiles render count:', totalProfilesCount, '| stable:', stableProfilesCount);
    try {
      console.table(ids.map((id, idx) => ({ idx, id })));
    } catch (_) {}
  }, [totalProfilesCount, stableProfilesCount, ads]);

  const getWidgetData = () => {
    // Return analyticsCount if exists and has data
    if (analyticsCount?.length > 0) {
      return analyticsCount;
    }
    return []; // ensure a safe return value
  };

  const getBannerImage = () => {
    // Get the first active banner from the API response
    // Note: The API interceptor extracts response.data, so we access bannerAds.ads directly
    if (bannerAds?.ads && bannerAds.ads.length > 0) {
      const activeBanner = bannerAds.ads.find(ad => ad.is_active);
      return activeBanner?.image_url || Banner; // fallback to static banner
    }
    return Banner; // fallback to static banner
  };

  const getBureauStatusUI = () => {
    if (!bureauData || bureauLoading) return null;

    const status = bureauData.status?.toLowerCase();
    
    // Only show UI for rejected status
    if (status === 'rejected') {
      return (
        <RejectedUi
          type="bureau_status"
          className="bureau_rejected"
          title="Bureau Application Rejected"
          description="Your bureau application has been rejected. Please contact support for more information or submit a new application with the required corrections."
        />
      );
    }

    return null;
  };

const navigate = useNavigate();
  return (
    <>
      {/* <Breadcrumb mainTitle="Lizant" pageTitle="Dashboard" /> */}

      <div className="dashboard_cnt block">
        <Warning />
        {getBureauStatusUI()}

        <Col xs={24} xxl={24}>
          <div>
            <Row className="banner_holder">
              <figure className='bg_img mb-0'>
                {loading ? (
                  <div style={{ 
                    height: '200px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px'
                  }}>
                    Loading banner...
                  </div>
                ) : (
                  <img 
                    src={getBannerImage()} 
                    alt="Banner" 
                    onError={(e) => {
                      e.target.src = Banner; // Fallback to static banner on error
                    }}
                  />
                )}
              </figure>
            </Row>
            <Row
              gutter={[24, 24]}
              style={{ marginTop: customStyles.margin }}
              className="dashboard_liveAds"
            >
              <Col lg={24} xs={24} style={{ marginTop: 0 }}><div className='profile_creation_wrap'>
              <NoResults
               type={'ad'}
               key={stableProfilesCount > 0 ? 'has-profiles' : 'no-profiles'}
               title={stableProfilesCount > 0 ? 'Add Your Next Profile' : 'Add a Profile'}
               description={stableProfilesCount > 0 ? 'Add more profiles to increase visibility and connect with potential matches.' : 'Get started on Lumi Bureau by adding a profile!'}>
               <Button className="wide mb" type='primary'
                   onClick={() => {
                       // if (!status) {
                       //     setProfileNotVerifiedModal(prev => !prev)
                       // } else {
                       //     navigate('/profile-creation');
                       // }
                       navigate('/profile-creation');
                   }}>Add New Profile <img src={AddIcon} alt="Add" /></Button>
              </NoResults>                                            
              </div></Col>
              
              
            </Row>

            <div style={{ marginTop: customStyles.margin }}>
              <h4 className="text_md mb">Summary</h4>
              <Row gutter={[24, 24]} className="dashboard_widget">
                <Widget data={getWidgetData()} />
              </Row>
            </div>
          </div>
        </Col>
      </div>
    </>
  );
};

export default Dashboard;
